import React from "react";
import LegalLayout, { LegalSection, ContactCard, LEGAL_EMAIL } from "./Privacy/LegalLayout";

const SECTIONS = [
  { id: "overview", title: "1. Overview" },
  { id: "information-we-collect", title: "2. Information We Collect" },
  { id: "how-we-use", title: "3. How We Use Your Information" },
  { id: "third-parties", title: "4. Third-Party Services" },
  { id: "sharing", title: "5. When We Share Information" },
  { id: "cookies", title: "6. Cookies & Analytics" },
  { id: "retention", title: "7. Data Retention" },
  { id: "security", title: "8. Security" },
  { id: "your-rights", title: "9. Your Rights" },
  { id: "children", title: "10. Children's Privacy" },
  { id: "changes", title: "11. Changes to This Policy" },
  { id: "contact", title: "12. Contact Us" },
];

const COLLECTED = [
  {
    label: "Account information",
    body: "Your name, email address, phone number, country, and password when you sign up, or basic profile details from Google if you sign in with Google.",
  },
  {
    label: "Application & enrollment details",
    body: "Information you provide when applying for a program, scholarship, partner program, or consultancy, including your background and goals.",
  },
  {
    label: "Learning activity",
    body: "Courses and cohorts you join, lesson and video progress, class attendance, assessments, assignments, project submissions, grades, points, and certificates earned.",
  },
  {
    label: "Career information",
    body: "CVs you upload for review, job preferences, and job board activity, used to provide career support and job matching.",
  },
  {
    label: "Payment information",
    body: "Records of payments, subscriptions, and installments. Card details are entered directly with our payment processors — we do not store your full card number.",
  },
  {
    label: "Community & communications",
    body: "Messages, comments, and posts you share in community spaces, and any correspondence you send to our team.",
  },
  {
    label: "Usage & device data",
    body: "Log data your browser sends us, such as IP address, browser type, pages visited, time and date of visits, and time spent on pages.",
  },
];

const PROVIDERS = [
  ["Paystack & PayPal", "Processing course, onboarding, and subscription payments"],
  ["Google", "Sign in with Google, analytics, and cloud file storage (Firebase)"],
  ["Zoom", "Hosting live classes, masterclasses, and webinars"],
  ["Vimeo", "Streaming recorded lesson videos"],
  ["Calendly", "Booking consultations and sessions"],
  ["AI service providers", "Generating feedback when you request a CV review"],
  ["Email delivery providers", "Sending account, class, and program notifications"],
];

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      eyebrow="Legal"
      title="Privacy Policy"
      intro="This policy explains what personal information E1X Academy collects when you use our website, learning platform, and community, how we use it, and the choices you have."
      lastUpdated="October 22, 2026"
      sections={SECTIONS}
    >
      <LegalSection id="overview" title="1. Overview">
        <p>
          E1X Academy (“we”, “us”, or the “Academy”) operates e1xacademy.net, CRM and its learning
          platform, including online and live courses, cohort programs, scholarships, masterclasses
          and webinars, the community, career services such as CV reviews and the job board, and
          our partner and consultancy programs (together, the “Services”).
        </p>
        
        <p>
          By using the Services, you agree to the collection and use of information as described
          in this policy. We will not use or share your information except as set out here.
        </p>
      </LegalSection>

      <LegalSection id="information-we-collect" title="2. Information We Collect">
        <p>Depending on how you use the Services, we may collect:</p>
        <dl className="divide-y divide-slate-100 rounded-xl border border-slate-200">
          {COLLECTED.map(({ label, body }) => (
            <div key={label} className="p-4 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt className="font-semibold text-slate-800">{label}</dt>
              <dd className="mt-1 sm:mt-0 sm:col-span-2">{body}</dd>
            </div>
          ))}
        </dl>
      </LegalSection>

      <LegalSection id="how-we-use" title="3. How We Use Your Information">
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Create and manage your account and verify your email address.</li>
          <li>Process applications, admissions, scholarships, and payments.</li>
          <li>Give you access to courses, live classes, recordings, and learning materials.</li>
          <li>Track your progress, grade your work, and issue certificates.</li>
          <li>Provide career support, including CV reviews and relevant job opportunities.</li>
          <li>Send class reminders, program updates, and — where you opt in — newsletters.</li>
          <li>Respond to your enquiries and provide support.</li>
          <li>Understand how the Services are used so we can improve them.</li>
          <li>Prevent fraud and misuse, and meet our legal obligations.</li>
        </ul>
      </LegalSection>

      <LegalSection id="third-parties" title="4. Third-Party Services">
        <p>
          We rely on trusted providers to run parts of the Services. They only receive the
          information needed for their task and are not permitted to use it for other purposes.
        </p>
        <div className="overflow-hidden rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-4 py-3 font-semibold">Provider</th>
                <th className="px-4 py-3 font-semibold">Purpose</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {PROVIDERS.map(([name, purpose]) => (
                <tr key={name}>
                  <td className="px-4 py-3 font-medium text-slate-800 align-top">{name}</td>
                  <td className="px-4 py-3">{purpose}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>
          These providers process information under their own privacy policies, which we
          encourage you to review.
        </p>
      </LegalSection>

      <LegalSection id="sharing" title="5. When We Share Information">
        <p>
          <strong className="text-slate-800">We do not sell your personal information.</strong> We
          only share it:
        </p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>With the service providers listed above, to operate the Services.</li>
          <li>With instructors and tutors, so they can teach, grade, and support you.</li>
          <li>
            With scholarship sponsors or hiring partners, only where you apply for or opt into
            those opportunities.
          </li>
          <li>With other members, for content you choose to post in the community.</li>
          <li>When required by law, or to protect the rights and safety of our users and the Academy.</li>
        </ul>
      </LegalSection>

      <LegalSection id="cookies" title="6. Cookies & Analytics">
        <p>
          Cookies are small files stored on your device. We use them, along with your browser's
          local storage, to keep you signed in, remember your preferences, and understand how
          visitors use our site through Google Analytics.
        </p>
        <p>
          You can block or delete cookies in your browser settings. If you do, some parts of the
          Services — such as staying signed in — may not work properly.
        </p>
      </LegalSection>

      <LegalSection id="retention" title="7. Data Retention">
        <p>
          We keep your information for as long as your account is active and as needed to provide
          the Services. Academic records, such as certificates and completion history, may be kept
          longer so we can verify your achievements. Payment records are kept as required for
          accounting and legal purposes.
        </p>
      </LegalSection>

      <LegalSection id="security" title="8. Security">
        <p>
          We use commercially reasonable measures to protect your information, including encrypted
          connections and restricted access to personal data. However, no method of transmission
          over the internet or electronic storage is completely secure, and we cannot guarantee
          absolute security.
        </p>
      </LegalSection>

      <LegalSection id="your-rights" title="9. Your Rights">
        <p>Subject to applicable data protection laws, you can ask us to:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Access the personal information we hold about you.</li>
          <li>Correct inaccurate or incomplete information.</li>
          <li>Delete your account and personal information.</li>
          <li>Stop sending you marketing emails (you can also unsubscribe from any newsletter).</li>
        </ul>
        <p>
          To make a request, email{" "}
          <a href={`mailto:${LEGAL_EMAIL}`} className="font-semibold text-[#d15424] hover:underline">
            {LEGAL_EMAIL}
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection id="children" title="10. Children's Privacy">
        <p>
          The Services are not directed at children under 13, and we do not knowingly collect
          their personal information. If you believe a child has provided us with information,
          please contact us and we will delete it.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="11. Changes to This Policy">
        <p>
          We may update this policy from time to time. Changes take effect when posted on this
          page, and the “Last updated” date above will be revised. Please review this page
          periodically.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="12. Contact Us">
        <ContactCard>
          Have a question or request about your privacy or this policy? Reach our team by email.
        </ContactCard>
      </LegalSection>
    </LegalLayout>
  );
}
